import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject, Optional } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CryptoService } from '../services/crypto.service';
import { SECURITY_CONFIG, SecurityConfig } from '../models/security-config.interface';

/** Decrypts responses using AES keys from headers (X-Request-Context, X-Client-Ref) */
@Injectable()
export class DecryptionInterceptor implements HttpInterceptor {
  constructor(
    private cryptoService: CryptoService,
    @Optional() @Inject(SECURITY_CONFIG) private config: SecurityConfig | null
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const cryptoService = this.cryptoService;
    const config = this.config;

    return next.handle(req).pipe(
      switchMap(event => {
      if (!(event instanceof HttpResponse)) return of(event);
      if (config?.enableEncryption === false) return of(event);

      const encryptPatterns = config?.encryptDecryptForEndpoints || ['*'];
      const skipPatterns = config?.skipEncryptDecryptForEndpoints;
      const shouldDecrypt = cryptoService.matchesPattern(req.url, encryptPatterns, skipPatterns);

      if (!shouldDecrypt) return of(event);
      if (config?.publicKeyEndpoint && req.url === config.publicKeyEndpoint) return of(event);

        return decryptResponse(event, cryptoService, config);
      }),
      catchError((error: HttpErrorResponse) => {
      // Handle error responses (4xx, 5xx)
      if (config?.enableEncryption === false) return throwError(() => error);

      const encryptPatterns = config?.encryptDecryptForEndpoints || ['*'];
      const skipPatterns = config?.skipEncryptDecryptForEndpoints;
      const shouldDecrypt = cryptoService.matchesPattern(req.url, encryptPatterns, skipPatterns);

      if (!shouldDecrypt) return throwError(() => error);
      if (config?.publicKeyEndpoint && req.url === config.publicKeyEndpoint) return throwError(() => error);
      // Check if error has encryption headers
      const aesKeyBase64 = error.headers?.get('X-Request-Context') || error.headers?.get('x-request-context');
      const ivBase64 = error.headers?.get('X-Client-Ref') || error.headers?.get('x-client-ref');

      if (!aesKeyBase64 || !ivBase64 || !error.error) {
        return throwError(() => error);
      }

      // Decrypt error response
      return from(
        cryptoService.decryptResponsePayload(
          typeof error.error === 'string' ? error.error : JSON.stringify(error.error),
          aesKeyBase64,
          ivBase64
        ).then(
          decrypted => {
            if (decrypted) {
              // Return decrypted error - spread decrypted data to top level for easier access
              // This allows components to access error.errors[0] instead of error.error.errors[0]
              const decryptedError = new HttpErrorResponse({
                error: decrypted,
                headers: error.headers,
                status: error.status,
                statusText: error.statusText,
                url: error.url || undefined
              });
              
              // Merge decrypted properties to top level of error object
              Object.assign(decryptedError, decrypted);
              
              throw decryptedError; // Throw the decrypted error to be caught by component
            }
            throw error; // Throw original error if decryption returned null
          },
          decryptError => {
            // Decryption failed - throw original error
            throw error;
          }
        )
      );
    })
    );
  }
}

/** Helper function to decrypt successful responses */
function decryptResponse(
  event: HttpResponse<any>,
  cryptoService: CryptoService,
  config: SecurityConfig | null
) {
  const aesKeyBase64 = event.headers.get('X-Request-Context') || event.headers.get('x-request-context');
  const ivBase64 = event.headers.get('X-Client-Ref') || event.headers.get('x-client-ref');
  if (!aesKeyBase64 || !ivBase64 || !event.body) {
    return of(event);
  }

  return from(
    cryptoService.decryptResponsePayload(event.body as string, aesKeyBase64, ivBase64)
  ).pipe(
    switchMap(decrypted => {
      if (decrypted) {
        return of(event.clone({ body: decrypted }));
      }
      return of(event);
    })
  );
}
